import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSuperpowers, FaUserPlus } from "react-icons/fa";
import {
  SiConvertio,
  SiLibreofficeimpress,
  SiMaterialformkdocs,
  SiSentry,
  SiStatuspage,
  SiSteelseries,
} from "react-icons/si";
import { ImLocation2 } from "react-icons/im";
import { RiToolsFill } from "react-icons/ri";
import { DiMootoolsBadge } from "react-icons/di";
import { VscGroupByRefType } from "react-icons/vsc";
import { TbBrandReason, TbReorder } from "react-icons/tb";
import {
  MdCategory,
  MdMenuOpen,
  MdSignalWifiStatusbar3Bar,
} from "react-icons/md";
import { LuBriefcaseConveyorBelt } from "react-icons/lu";
import { GiFurnace, GiSpinalCoil } from "react-icons/gi";
import { FaPerson, FaPersonBreastfeeding } from "react-icons/fa6";
import { FcDepartment } from "react-icons/fc";

const masterSections = [
  {
    category: "Die Management",
    items: [
      {
        title: "Die Status",
        route: "/master/die-management/die-status",
        icon: SiStatuspage,
        color: "text-blue-600",
      },
      {
        title: "Die Supplier",
        route: "/master/die-management/supplier-die",
        icon: FaSuperpowers,
        color: "text-indigo-600",
      },
      {
        title: "Location",
        route: "/master/die-management/location",
        icon: ImLocation2,
        color: "text-emerald-600",
      },
      {
        title: "Tooling Size",
        route: "/master/die-management/toolingSize",
        icon: RiToolsFill,
        color: "text-orange-600",
      },
      {
        title: "Tooling-Type",
        route: "/master/tooling-type",
        icon: DiMootoolsBadge,
        color: "text-cyan-600",
      },
      {
        title: "Die Type",
        route: "/master/die-management/dieType",
        icon: VscGroupByRefType,
        color: "text-violet-600",
      },
      {
        title: "DieUnload Reason",
        route: "/master/dieUnloadReason",
        icon: TbBrandReason,
        color: "text-rose-600",
      },
      {
        title: "Tooling-Entry",
        route: "/master/tooling-Entry",
        icon: SiSentry,
        color: "text-red-500",
      },
    ],
  },
  {
    category: "Production & Sales",
    items: [
      {
        title: "Customer",
        route: "/master/sales-customer",
        icon: FaPerson,
        color: "text-teal-600",
      },
      {
        title: "Alloy",
        route: "/master/sales-orders/alloy",
        icon: TbReorder,
        color: "text-amber-600",
      },
      {
        title: "Order Status",
        route: "/master/sales-orders/order-status",
        icon: MdSignalWifiStatusbar3Bar,
        color: "text-blue-500",
      },
      {
        title: "Unit Conversion",
        route: "/master/sales-orders/unit-conversion",
        icon: SiConvertio,
        color: "text-purple-600",
      },
      {
        title: "Spectro",
        route: "/master/spectro",
        icon: LuBriefcaseConveyorBelt,
        color: "text-slate-600",
      },
      {
        title: "Furnace",
        route: "/master/furnace",
        icon: GiFurnace,
        color: "text-red-600",
      },
      {
        title: "Series",
        route: "/master/series",
        icon: SiSteelseries,
        color: "text-gray-700",
      },
      {
        title: "Press",
        route: "/master/press",
        icon: SiLibreofficeimpress,
        color: "text-sky-600",
      },
    ],
  },
  {
    category: "General Masters",
    items: [
      {
        title: "Raw Material",
        route: "/master/raw-material",
        icon: SiMaterialformkdocs,
        color: "text-lime-700",
      },
      {
        title: "Department",
        route: "/master/department",
        icon: FcDepartment,
        color: "text-blue-700",
      },
      {
        title: "Incharge",
        route: "/master/incharge",
        icon: FaPersonBreastfeeding,
        color: "text-pink-600",
      },
      {
        title: "Category",
        route: "/master/category",
        icon: MdCategory,
        color: "text-orange-500",
      },
      {
        title: "Nalco",
        route: "/master/settings/nalco-master",
        icon: GiSpinalCoil,
        color: "text-green-600",
      },
    ],
  },
  {
    category: "System Settings",
    items: [
      {
        title: "User",
        route: "/master/settings/users",
        icon: FaUserPlus,
        color: "text-indigo-700",
      },
      {
        title: "Menu",
        route: "/master/settings/menu-master",
        icon: MdMenuOpen,
        color: "text-slate-700",
      },
    ],
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const iconVariants = {
  hover: {
    scale: 1.2,
    rotate: [0, -10, 10, -10, 0],
    transition: {
      duration: 0.5,
      repeat: Infinity,
      repeatType: "reverse",
    },
  },
};

export default function SetUp() {
  const navigate = useNavigate();

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col">
          <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight">
            Master Data Registry
          </h2>
          <p className="text-sm text-gray-500">
            Configure and manage your system's core parameters and masters.
          </p>
        </div>
      </div>
      <div className="space-y-10">
        {masterSections.map((section) => (
          <div key={section.category}>
            <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4 ml-1">
              {section.category}
            </h3>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4"
            >
              {section.items.map((card) => {
                const Icon = card.icon;
                return (
                  <motion.button
                    key={card.title}
                    variants={itemVariants}
                    onClick={() => navigate(card.route)}
                    whileHover="hover"
                    whileTap={{ scale: 0.95 }}
                    className="group flex flex-col items-center justify-center p-5 bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:border-indigo-300 transition-all duration-300 relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 -mr-4 -mt-4 w-12 h-12 bg-gray-50 rounded-full group-hover:bg-indigo-50 transition-colors" />

                    <motion.div
                      variants={iconVariants}
                      className={`relative z-10 p-4 rounded-2xl bg-gray-50 group-hover:bg-white shadow-sm group-hover:shadow-indigo-100 transition-colors mb-4`}
                    >
                      <Icon
                        className={`text-2xl ${card.color} transition-transform duration-300`}
                      />
                    </motion.div>

                    <h4 className="text-[14px] font-bold text-gray-700 group-hover:text-indigo-600 text-center leading-tight relative z-10">
                      {card.title}
                    </h4>
                  </motion.button>
                );
              })}
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}
